import { useMemo } from 'react'

import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { type ResultOf } from 'gql.tada'
import * as _ from 'lodash-es'

import type {
  ExternalFundFissionTicker,
  RedeemableTokenJSON,
  RedeemableTokenSymbol,
} from '@repo/cms/cms_types'
import { graphql_call } from '@repo/common/queries/schemas/server_user_query'

import '../../helpers/refetch_helpers'
import { standard_long_api_wait } from '../../helpers/refetch_helpers'
import {
  disableKeyWhenMissing,
  type DisableKeyWhenMissingFlag,
} from '../../helpers/utilities'
import { useGetNetwork, useMultiAccount } from '../blockchain/management'

import {
  query_list_redeemables,
  query_list_redeemables_commission_fees,
  query_redemptions,
} from './redeemables_graphql'

export const keys = createQueryKeyStore({
  redeemables: {
    configurations: null,
    commission_fees: null,
    cms: null,
    prices: (
      p_args:
        | { symbol: string; redeemable_id: RubyID }
        | DisableKeyWhenMissingFlag,
    ) => ({
      queryKey: [p_args],
    }),
    latest_redemptions: (
      r_args:
        | { network: string; wallet_address: string }
        | DisableKeyWhenMissingFlag,
    ) => ({
      queryKey: [r_args],
    }),
  },
})

export type RedeemableSymbol =
  | NonNullable<
      ResultOf<typeof query_list_redeemables>['redeemables'][number]['symbol']
    >
  | 'USDC'

export type RedeemableCommissionFeesData = ResultOf<
  typeof query_list_redeemables_commission_fees
>['redeemables'][number]

export type RedeemableData = Omit<
  ResultOf<typeof query_list_redeemables>['redeemables'][number],
  keyof RedeemableCommissionFeesData
> & { id: RubyID }

export type RedeemablePriceFeedProtocol = ResultOf<
  typeof query_list_redeemables
>['redeemables'][number]['price_feed_protocol']

export function useListRedeemables() {
  const queryClient = useQueryClient()

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const { unpublished = false } = useSearch({ strict: false }) as {
    unpublished?: boolean
  }

  return useQuery<RedeemableData[]>({
    queryKey: keys.redeemables.configurations.queryKey,
    queryFn: async () => {
      const commission_key = keys.redeemables.commission_fees.queryKey

      await queryClient.cancelQueries({ queryKey: commission_key })

      const raw_results = await graphql_call({
        query: query_list_redeemables,
      })

      const commission_results: RedeemableCommissionFeesData[] = []

      let results = raw_results.map((record) => {
        const {
          id,
          name,
          symbol,
          published,
          networks,
          price_feed_protocol,
          price_feed,
          rate_adjustment,
          ...fees
        } = record
        commission_results.push({ id, ...fees })

        return {
          id,
          name,
          symbol,
          networks,
          price_feed_protocol,
          price_feed,
          rate_adjustment,
          published,
        } satisfies RedeemableData
      })

      if (!unpublished) {
        results = results.filter((r) => r.published)
      }

      await queryClient.setQueryData(
        commission_key,
        process_commission_fee_data(commission_results),
      )

      return results
    },
    gcTime: Infinity,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  })
}

export type BlockchainNetwork = RedeemableData['networks'][number]['network']

export function useGetRedeemable(id: RubyID | undefined): {
  data: RedeemableData | undefined
  isSuccess: boolean
  isLoading: boolean
  isError: boolean
  error: null | Error
} {
  const { data, isLoading, isError, error } = useListRedeemables()

  if (data == null) {
    return { data: undefined, isLoading, isError, error, isSuccess: false }
  }

  if (id == null) {
    return {
      data: undefined,
      isLoading: true,
      isError,
      error,
      isSuccess: false,
    }
  }

  return {
    data: data.find((t) => t.id == id),
    isLoading,
    isSuccess: true,
    isError,
    error,
  }
}

function process_commission_fee_data(data: RedeemableCommissionFeesData[]) {
  // ensure fee schedule entries are sorted
  data.forEach((r) => {
    r.commission_fee_info.fee_schedule.entries = _.sortBy(
      r.commission_fee_info.fee_schedule.entries,
      (e) => e.days_until,
    )
  })
  return data
}

export function useListRedeemableCommissionFees() {
  return useQuery<RedeemableCommissionFeesData[]>({
    queryKey: keys.redeemables.commission_fees.queryKey,
    queryFn: async () => {
      const results = await graphql_call({
        query: query_list_redeemables_commission_fees,
      })

      return process_commission_fee_data(results)
    },
    staleTime: 6 * 60 * 1000, // 6 minutes
  })
}

export function useGetRedeemableCommissionFee(id?: RubyID): {
  data: RedeemableCommissionFeesData | undefined
  isSuccess: boolean
  isLoading: boolean
  isError: boolean
  error: null | Error
} {
  const { data, isLoading, isError, error } = useListRedeemableCommissionFees()

  if (data == null || id == null) {
    return { data: undefined, isLoading, isError, error, isSuccess: false }
  }

  return {
    data: data.find((t) => t.id == id),
    isLoading,
    isSuccess: true,
    isError,
    error,
  }
}

export type RedeemableTokenData = {
  redeemable_id: RubyID
  symbol: RedeemableSymbol
  network: BlockchainNetwork
  decimals: number
  address: CryptoAddress
}

export function useGetRedeemableNetworks(): BlockchainNetwork[] {
  const { data: redeemables_data } = useListRedeemables()

  return useMemo(() => {
    if (!redeemables_data) return []
    return _.uniq(
      redeemables_data.flatMap((r) => {
        return r.networks.map((n) => n.network)
      }),
    )
  }, [redeemables_data])
}

export function useGetRedeemableTokens({
  include_settlement = false,
}: { include_settlement?: boolean } = {}): RedeemableTokenData[] {
  const { data: redeemables_data } = useListRedeemables()

  return useMemo(() => {
    if (!redeemables_data) return []
    const pairs = redeemables_data.flatMap((r) => {
      const p = r.networks.map((n) => ({
        ...n.deposit_token,
        redeemable_id: r.id,
        symbol: n.deposit_token.symbol as RedeemableSymbol,
        network: n.network,
      }))
      if (include_settlement) {
        p.push(
          ...r.networks.map((n) => ({
            ...n.settlement_token,
            redeemable_id: r.id,
            symbol: n.settlement_token.symbol as RedeemableSymbol,
            network: n.network,
          })),
        )
      }
      return p
    })
    return _.uniqBy(pairs, (p) => `${p.symbol}-${p.network}`)
  }, [include_settlement, redeemables_data])
}

export function useGetRedeemToken({
  token_symbol,
  network,
}: {
  token_symbol?: RedeemableSymbol
  network: BlockchainNetwork | null
}): RedeemableTokenData | undefined {
  const tokens = useGetRedeemableTokens({ include_settlement: true })

  if (tokens.length == 0 || !token_symbol || network == null) return undefined

  const token = tokens.find(
    (r) => r.symbol === token_symbol && r.network === network,
  )

  return token
}

export type RedemptionCalledData = ResultOf<
  typeof query_redemptions
>['redemptions']['nodes'][number]

export type RedemptionData = RedemptionCalledData & {
  settlement_total: number
}

export type RedemptionStatus =
  | ResultOf<typeof query_redemptions>['redemptions']['nodes'][number]['status']
  | 'send_transaction_failure'

export const StatusToHumanString: Record<RedemptionStatus, string> = {
  awaiting: 'Approve in Wallet',
  receiving: 'Verifying Receipt',
  processing: 'Processing',
  provisioning: 'Sourcing',
  converting: 'Bridging',
  settling: 'Sending Payout',
  completed: 'Redemption Complete',
  reversing: 'Error - Returning Tokens',
  reversed: 'Error - Tokens Returned',
  failed: 'Error - Contact Support',
  send_transaction_failure: 'Error - Sending Failed',
}

export const reversal_statuses: RedemptionStatus[] = ['reversing', 'reversed']

export const failure_states: RedemptionStatus[] = [
  ...reversal_statuses,
  'failed',
  'send_transaction_failure',
]

export const final_states: RedemptionStatus[] = [
  'completed',
  'reversed',
  'failed',
  'send_transaction_failure',
]

export const StatusToStageMap: Record<
  RedemptionStatus,
  { value: number; stage: number; message: string; short_message: string }
> = {
  awaiting: {
    value: 10,
    stage: 0,
    message: 'Waiting for approval in wallet…',
    short_message: 'Signing…',
  },
  receiving: {
    value: 25,
    stage: 1,
    message: 'Verifying Receipt…',
    short_message: 'Verifying…',
  },
  processing: {
    value: 40,
    stage: 2,
    message: 'Processing Redemption…',
    short_message: 'Processing…',
  },
  provisioning: {
    value: 50,
    stage: 2,
    message: 'Sourcing Redemption…',
    short_message: 'Sourcing…',
  },
  converting: {
    value: 60,
    stage: 2,
    message: 'Bridging Redemption…',
    short_message: 'Bridging…',
  },
  settling: {
    value: 75,
    stage: 3,
    message: 'Sending Payout…',
    short_message: 'Sending…',
  },
  reversing: {
    value: 75,
    stage: 3,
    message: 'Redemption Failed - Returning Tokens…',
    short_message: 'Returning…',
  },
  completed: {
    value: 100,
    stage: 4,
    message: 'Redemption Complete',
    short_message: 'Complete',
  },
  reversed: {
    value: 100,
    stage: 4,
    message: 'Redemption Failed - Tokens Returned',
    short_message: 'Returned',
  },
  failed: {
    value: 100,
    stage: 2,
    message: 'Redemption Failed - Contact Support',
    short_message: 'Failed',
  },
  send_transaction_failure: {
    value: 100,
    stage: 0,
    message: 'Redemption Failed - Sending Failed',
    short_message: 'Failed',
  },
}

const default_error = 'Internal error'
export type RedemptionErrorType = RedemptionData['error_reason']
export const redemption_error_to_message: Record<
  NonNullable<RedemptionErrorType>,
  string
> = {
  commission_fee_exceeded: 'Slippage exceeded',
  conversion_configuration_error: default_error,
  conversion_failed: default_error,
  expired: 'Redemption expired',
  insufficient_liquidity: 'Insufficient liquidity',
  internal: default_error,
  onchain_rejected: default_error,
  oracle_unavailable: 'Price feed unavailable',
  provisioning_failed: default_error,
  redemption_capacity_exceeded: 'Redemption capacity exceeded',
  settlement_min: 'Net settlement below minimum',
  unmatched_deposit: 'Unexpected deposit',
} as const

export function get_redemption_error_message(
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  error_reason?: RedemptionErrorType | string,
): string {
  if (error_reason == null || !(error_reason in redemption_error_to_message))
    return default_error

  return redemption_error_to_message[
    error_reason as keyof typeof redemption_error_to_message
  ]
}

export function useLatestRedemptions() {
  const { wallet_address } = useMultiAccount()
  const network = useGetNetwork()

  const enabled = wallet_address != null && network != null

  return useQuery<RedemptionData[]>({
    queryKey: keys.redeemables.latest_redemptions(
      disableKeyWhenMissing(enabled ? { wallet_address, network } : undefined),
    ).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const records = await graphql_call({
            query: query_redemptions,
            variables: { wallet_address: wallet_address as CryptoAddress },
          })

          return _.sortBy(records.nodes, [
            (r) => StatusToStageMap[r.status].value,
            (r) => r.settled_at ?? 'z',
          ])
        },
    staleTime: 60 * 1000, // 60 seconds
    refetchInterval: (query) => {
      const all_final = query.state.data?.every((r) =>
        final_states.includes(r.status),
      )

      if (all_final) {
        return false
      }

      return standard_long_api_wait()
    },
  })
}

export function useGetRedemptionRecord(id?: RubyID): {
  data: RedemptionData | undefined
  isSuccess: boolean
  isLoading: boolean
  isError: boolean
  refetch: ReturnType<typeof useLatestRedemptions>['refetch']
  error: null | Error
} {
  const { data, isLoading, isError, error, refetch } = useLatestRedemptions()

  const record = data?.find((t) => t.id == id)

  if (data == null) {
    return {
      data: undefined,
      isLoading,
      isError,
      error,
      refetch,
      isSuccess: false,
    }
  }

  return {
    data: record,
    isLoading,
    isSuccess: true,
    refetch,
    isError,
    error,
  }
}

export function useGetAllRedeemableCMS() {
  return useQuery({
    queryKey: keys.redeemables.cms.queryKey,
    queryFn: async () => {
      return (await import('@repo/cms/data/redeemables/redeemable_tokens.json'))
        .default as RedeemableTokenJSON
    },
    gcTime: Infinity,
    staleTime: 1000 * 60 * 60 * 24, // one day
  })
}

export function useGetRedeemableCMS({ symbol }: { symbol: string }) {
  const { data: all_data, isLoading, isError, error } = useGetAllRedeemableCMS()

  let data = all_data?.[symbol as RedeemableTokenSymbol]
  const isSuccess = data != null

  if (all_data != null && data == null) {
    data = {
      symbol: symbol as RedeemableTokenSymbol,
      fund_name: symbol,
      issued_by: 'Fission Test',
      investment_manager: 'Fission Test' as ExternalFundFissionTicker,
    }
  }

  return {
    data,
    isLoading,
    isSuccess,
    isError,
    error,
  }
}
